import { useState, useRef, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutContent } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '男同网',
    url: 'https://nantong.wang',
    description: '专为男同群体打造的综合性平台，提供交友认识、社群互动、生活资讯、健康指南及活动资讯。',
    foundingDate: '2020',
    email: 'contact@nantong.wang',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@nantong.wang',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutContent.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      return;
    }
    formData.delete('phone_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9sn64d8o1vn02ekuia0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const code = (parsed?.code as string) || '';
      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
        || (parsed?.message as string) || responseText || '';

      if (res.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试。');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-4">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {aboutContent.intro.title}
              </h1>
            </div>
          </div>
        </div>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-4">
                  {aboutContent.intro.subtitle}
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  {aboutContent.intro.description}
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  <strong>{aboutContent.intro.mission}</strong>
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  {aboutContent.intro.vision}
                </p>
              </div>
              <div className="flex-1">
                <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden">
                  <img
                    src={aboutContent.intro.imageUrl}
                    alt="男同网 - 温暖包容的社区"
                    title="男同网社区氛围"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                发展历程
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">从创立至今，我们一步一个脚印</p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="flex flex-col gap-6">
                {aboutContent.timeline.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="hidden md:flex flex-1" />
                    <div className="absolute left-2.5 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 md:-translate-x-1.5 z-10 mt-1.5"></div>
                    <div className="pl-10 md:pl-0 md:flex-1">
                      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
                        <span className="text-sm font-bold text-primary-500">{item.year}</span>
                        <h3 className="text-base font-semibold text-foreground-900 mt-1">{item.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">来自社群成员的真实声音</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {aboutContent.testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatarUrl}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.age}岁 · {t.city}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                {aboutContent.contact.title}
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                {aboutContent.contact.description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-5 bg-background-50 border border-background-200/70 rounded-lg">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center text-primary-500">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <div className="text-xs text-foreground-400 mb-1">邮箱</div>
                <div className="text-sm font-medium text-foreground-800">{aboutContent.contact.email}</div>
              </div>
              <div className="text-center p-5 bg-background-50 border border-background-200/70 rounded-lg">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center text-primary-500">
                  <i className="ri-wechat-line text-xl"></i>
                </div>
                <div className="text-xs text-foreground-400 mb-1">微信公众号</div>
                <div className="text-sm font-medium text-foreground-800">{aboutContent.contact.wechat}</div>
              </div>
              <div className="text-center p-5 bg-background-50 border border-background-200/70 rounded-lg">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center text-primary-500">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <div className="text-xs text-foreground-400 mb-1">地址</div>
                <div className="text-sm font-medium text-foreground-800">{aboutContent.contact.address}</div>
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
              <h3 className="text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>

              {formStatus === 'success' && (
                <div className="mb-6 p-4 bg-primary-50 text-primary-700 rounded-lg text-sm">
                  感谢你的留言！我们会在24小时内回复你。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg text-sm">
                  {formError}
                </div>
              )}

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-4"
              >
                <div className="honeypot-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入你的称呼"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-700">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    className="px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700">
                    消息内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="px-4 py-2.5 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入你想说的话（500字以内）"
                  ></textarea>
                  <span className="text-xs text-foreground-400">最多500个字符</span>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed self-start"
                >
                  {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                </button>
              </form>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                常见问题
              </h2>
            </div>
            <div className="flex flex-col gap-3">
              {aboutContent.faqs.map((faq, index) => (
                <details key={index} className="border border-background-200/70 rounded-lg overflow-hidden group">
                  <summary className="p-4 md:p-5 cursor-pointer text-sm md:text-base font-medium text-foreground-800 hover:bg-background-50 transition-colors list-none flex items-center justify-between">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform duration-300 flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}